/**
 * BindTotpPage — accept-invite 后首登必经的 TOTP 绑定页（codex P1 #2 配套）
 *
 * Flow（逻辑保持不变）：
 * 1. login(email,password) 返 stage='bind_totp_required' + bind_totp_token
 * 2. 跳本页（query: token=<bind_totp_token>）
 * 3. getTotpSetup(token) 拉 secret + otpauth_uri，渲染可扫 QR + 手动 secret
 * 4. 用户在 Authenticator App 扫码后输 6 位 TOTP code → bindTotp(token, code)
 * 5. 服务端校验 + 标 totp_enabled + issue 完整 access+refresh
 * 6. 客户端写 auth-store + 一次性展示 10 个 backup codes → 确认后跳 from / dashboard
 *
 * 皮肤 1:1 移植 design/Novel-admin/Login.html step-2-setup（QR + 三步引导 + 密钥行 + 6 格 OTP）。
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AdminApiError } from '~/api/errors';
import { applyLoginDefaults } from '~/app/app-scope-store';
import { bindTotp, getTotpSetup } from '~/hooks/use-auth';
import { QrCode } from '~/lib/qr';
import { useAuthStore } from '~/stores/auth-store';
import { Dialog, DialogBody } from '~/ui/Dialog';
import { toast } from '~/ui/Toast';

export default function BindTotpPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const fromPath = params.get('from') ?? '/';
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  // 登录第一步经 router state 传来的邮箱 + 「14 天保持登录」勾选（刷新会丢，按缺省兜底）
  const location = useLocation();
  const navState = location.state as { email?: string; remember?: boolean } | null;
  const email = navState?.email ?? '';
  // 「14 天保持登录」勾选经 URL query 透传（router state 整页刷新会丢、query 不丢）；
  // 缺省回退 router state、再回退登录页默认勾选(true)，避免刷新后误降级为 7 天 refresh
  const rememberParam = params.get('remember');
  const remember = rememberParam != null ? rememberParam === '1' : (navState?.remember ?? true);
  const displayName = email ? email.split('@')[0] : '新运营账户';
  const avatarInitial = (email[0] ?? 'N').toUpperCase();

  const [submitting, setSubmitting] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  // codex round-3 #12：重新拉 OTP URI 让用户即使关了 AcceptInvitePage 也能扫码
  const [setup, setSetup] = useState<{ secret_base32: string; otpauth_uri: string } | null>(null);
  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [copied, setCopied] = useState(false);
  // 临时 token 过期/失效（5 分钟）时拉 OTP setup 会失败，此时引导回登录而非卡死
  const [loadFailed, setLoadFailed] = useState(false);
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (!token) return;
    getTotpSetup(token)
      .then(setSetup)
      .catch((e: unknown) => {
        setLoadFailed(true);
        if (e instanceof AdminApiError) toast(`恢复 OTP URI 失败：${e.message}`, { tone: 'neg' });
      });
  }, [token]);

  const doBind = async (code: string) => {
    setSubmitting(true);
    try {
      const res = await bindTotp(token, code, { remember });
      setSession({ access: res.access, refresh: res.refresh, user: res.user });
      setBackupCodes(res.backup_codes);
    } catch (e) {
      const message =
        e instanceof AdminApiError ? e.message : '验证码错误，请检查验证器中的最新代码';
      setError(true);
      setErrorMessage(message);
      setTimeout(() => {
        setError(false);
        setErrorMessage('');
        setDigits(['', '', '', '', '', '']);
        inputsRef.current[0]?.focus();
      }, 1200);
      toast(message, { tone: 'neg' });
    } finally {
      setSubmitting(false);
    }
  };

  /** 写入第 i 格并自动跳焦；6 格填满自动绑定 */
  const setDigit = (i: number, raw: string) => {
    const v = raw.replace(/[^0-9]/g, '').slice(-1);
    const next = [...digits];
    next[i] = v;
    setDigits(next);
    setError(false);
    setErrorMessage('');
    if (v && i < 5) inputsRef.current[i + 1]?.focus();
    if (next.every((d) => d) && !submitting) void doBind(next.join(''));
  };

  const onKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) inputsRef.current[i - 1]?.focus();
    else if (e.key === 'ArrowLeft' && i > 0) inputsRef.current[i - 1]?.focus();
    else if (e.key === 'ArrowRight' && i < 5) inputsRef.current[i + 1]?.focus();
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const text = e.clipboardData
      .getData('text')
      .replace(/[^0-9]/g, '')
      .slice(0, 6);
    if (!text) return;
    const next = ['', '', '', '', '', ''];
    text.split('').forEach((ch, j) => {
      next[j] = ch;
    });
    setDigits(next);
    inputsRef.current[Math.min(text.length, 5)]?.focus();
    if (text.length === 6 && !submitting) void doBind(text);
  };

  const copySecret = () => {
    if (!setup) return;
    if (navigator.clipboard) navigator.clipboard.writeText(setup.secret_base32).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };

  if (!token || loadFailed) {
    return (
      <div className="step">
        <div style={{ marginBottom: 18 }}>
          <h2 className="form-title">绑定双重验证</h2>
          <p className="form-sub">
            {loadFailed
              ? '临时登录已失效（5 分钟内有效），请重新登录'
              : '临时 token 缺失，请重新登录'}
          </p>
        </div>
        <button type="button" className="btn-big" onClick={() => navigate('/auth/login')}>
          重新登录
        </button>
      </div>
    );
  }

  const allFilled = digits.every((d) => d);
  // 手动密钥每 4 字符分组展示（与原型一致）
  const secretGrouped = setup
    ? (setup.secret_base32.match(/.{1,4}/g)?.join(' ') ?? setup.secret_base32)
    : '';

  return (
    <>
      <div className="step-indicator">
        <span>绑定 2FA</span>
        <span className="step-dot done" />
        <span className="step-dot active" />
      </div>

      <div className="step">
        <div style={{ marginBottom: 18 }}>
          <h2 className="form-title">绑定双重验证</h2>
          <p className="form-sub">首次登录需要绑定一次性密码 (OTP) 验证器，以保护你的账户</p>
        </div>

        {/* 用户卡片 — 正在为哪个账户绑定 + 未绑定状态 + 返回登录（对齐设计稿 step-2-setup）*/}
        <div className="twofa-user" style={{ marginBottom: 16 }}>
          <div className="twofa-avatar">{avatarInitial}</div>
          <div className="twofa-info">
            <div className="twofa-name">{displayName}</div>
            {email ? <div className="twofa-mail">{email}</div> : null}
          </div>
          <span className="chip chip-warn" style={{ fontSize: 10 }}>
            未绑定
          </span>
          <button
            type="button"
            className="btn-sso"
            style={{ padding: '0 10px', height: 28, fontSize: 11, flexShrink: 0 }}
            title="返回"
            onClick={() => navigate('/auth/login')}
          >
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m15 18-6-6 6-6" />
            </svg>
            返回
          </button>
        </div>

        <div className="setup-grid">
          <div className="qr-frame">
            {setup ? <QrCode value={setup.otpauth_uri} size={136} /> : null}
          </div>
          <div className="setup-steps">
            <div className="setup-step">
              <span className="setup-step-num">1</span>
              <div>
                <div className="setup-step-title">下载验证器 App</div>
                <div className="setup-step-desc">Google Authenticator · Authy · 1Password</div>
                <div className="store-row">
                  <a
                    className="store-btn"
                    href="https://apps.apple.com/app/google-authenticator/id388497605"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    App Store
                  </a>
                  <a
                    className="store-btn"
                    href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Google Play
                  </a>
                </div>
              </div>
            </div>
            <div className="setup-step">
              <span className="setup-step-num">2</span>
              <div>
                <div className="setup-step-title">扫描二维码</div>
                <div className="setup-step-desc">在 App 中点「添加」→「扫码」</div>
              </div>
            </div>
            <div className="setup-step">
              <span className="setup-step-num">3</span>
              <div>
                <div className="setup-step-title">输入 6 位代码确认</div>
                <div className="setup-step-desc">代码每 30 秒刷新</div>
              </div>
            </div>
          </div>
        </div>

        {setup && (
          <div className="qr-secret-row">
            <span style={{ fontSize: 11, color: 'var(--fg-muted)', flexShrink: 0 }}>
              无法扫码？密钥
            </span>
            <code className="qr-secret">{secretGrouped}</code>
            <button type="button" className="qr-secret-copy" onClick={copySecret}>
              {copied ? (
                <>
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                  已复制
                </>
              ) : (
                <>
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="9" y="9" width="13" height="13" rx="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                  复制
                </>
              )}
            </button>
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            const code = digits.join('');
            if (code.length === 6) void doBind(code);
          }}
          style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
        >
          <div className="field">
            <label className="field-label">输入 Authenticator 显示的 6 位代码以完成绑定</label>
            <div className="otp-row">
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => {
                    inputsRef.current[i] = el;
                  }}
                  className={`otp-input${d ? ' filled' : ''}${error ? ' error' : ''}`}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  autoComplete="one-time-code"
                  value={d}
                  onChange={(e) => setDigit(i, e.target.value)}
                  onKeyDown={(e) => onKeyDown(i, e)}
                  onPaste={onPaste}
                  onFocus={(e) => e.target.select()}
                />
              ))}
            </div>
          </div>
          {error && (
            <div className="err">
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="9" />
                <path d="M12 8v4" />
                <path d="M12 16h.01" />
              </svg>
              <span>{errorMessage || '验证码错误，请检查验证器中的最新代码'}</span>
            </div>
          )}
          <button type="submit" className="btn-big" disabled={!allFilled || submitting}>
            {submitting ? '绑定中…' : allFilled ? '确认绑定并登录' : '输入 6 位代码完成绑定'}
          </button>
        </form>
      </div>

      {backupCodes !== null && (
        <Dialog
          title="保存备份码（仅显示一次）"
          subtitle="每个备份码仅可使用 1 次，用于丢手机时登录兜底"
          icon="IconShield"
          width={420}
          onClose={() => {}}
          footer={
            <button
              type="button"
              className="btn btn-accent"
              onClick={() => {
                setBackupCodes(null);
                // 套用本机默认 App scope；from 无深链时跳默认落地页
                const defaultPage = applyLoginDefaults();
                navigate(fromPath !== '/' ? fromPath : defaultPage, { replace: true });
              }}
            >
              我已保存，进入后台
            </button>
          }
        >
          <DialogBody>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <p className="muted" style={{ fontSize: 12.5, lineHeight: 1.6, margin: 0 }}>
                以下 10 个备份码请保存到密码管理器；关闭本对话框后无法再次查看（备份码 hash
                入库，明文不持久化）。
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                {backupCodes.map((c) => (
                  <code
                    key={c}
                    className="mono"
                    style={{
                      fontSize: 13,
                      padding: '8px 10px',
                      background: 'var(--bg-subtle)',
                      border: '1px solid var(--border)',
                      borderRadius: 6,
                      textAlign: 'center',
                      letterSpacing: '0.04em',
                    }}
                  >
                    {c}
                  </code>
                ))}
              </div>
              <div className="err" style={{ marginTop: 4 }}>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 8v4" />
                  <path d="M12 16h.01" />
                </svg>
                <span>关闭后无法再次查看备份码</span>
              </div>
            </div>
          </DialogBody>
        </Dialog>
      )}
    </>
  );
}
